import * as React from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';
import { useRetracked } from 'js/lib/retracked';

import { SEARCH_MAIN_INDEX_ID } from 'bundles/search-common/constants';
import { useSearch } from 'bundles/search-common/providers/SearchContext';

import _t from 'i18n!nls/coursera-plus';

import 'css!./__styles__/InPageNumberOfResultsSection';

type Props = {
  limitNumberOfResults: number;
};

const InPageNumberOfResultsSection = ({ limitNumberOfResults }: Props) => {
  const track = useRetracked();
  const { getSearchResults, isLoading } = useSearch();

  const searchResults = getSearchResults(SEARCH_MAIN_INDEX_ID);
  const { query = '', hitsTotalCount = 0 } = searchResults || {};

  // Track the search results when query changes
  React.useEffect(() => {
    if (query) {
      track({
        trackingData: {
          total_number_of_search_results: hitsTotalCount,
          query,
        },
        trackingName: 'total_number_of_search_results',
        action: 'search',
      });
    }
  }, [query, hitsTotalCount, track]);

  const renderMessage = () => {
    if (isLoading) {
      return _t('Loading search results...');
    }

    if (hitsTotalCount > 0) {
      return (
        <FormattedMessage
          message={
            query
              ? _t('Showing {limitNumberOfResults} of {numberOfHits} total results for "{query}"')
              : _t('Showing {numberOfHits} total results')
          }
          limitNumberOfResults={Math.min(limitNumberOfResults, hitsTotalCount)}
          numberOfHits={hitsTotalCount}
          query={query}
        />
      );
    } else {
      return (
        <FormattedMessage
          message={query ? _t('No results found for "{query}"') : _t('No results found for your search')}
          query={query}
        />
      );
    }
  };

  return (
    <h3
      data-e2e="NumberOfResultsSection"
      className="rc-NumberOfResultsSection body-2-text"
      role="status"
      aria-live="polite"
    >
      {renderMessage()}
    </h3>
  );
};

export default InPageNumberOfResultsSection;
